import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import ActivityExplorer from '../../components/ActivityExplorer';
import ScheduleTimeline from '../../components/ScheduleTimeline';
import ActivityMappingStudio from '../../components/ActivityMappingStudio';
import ActivityDetailDrawer from '../../components/ActivityDetailDrawer';
import { CalendarClock, Loader2, Upload, ChevronLeft, CalendarDays, AlertTriangle, Sun } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const dt = (v) => (v ? String(v).slice(0, 10) : '—');

const ScheduleIntelligence = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'CompanyAdmin' || user?.role === 'SuperAdmin';
  const canMap = isAdmin;

  const TABS = [
    { k: 'dashboard', l: 'Dashboard' }, { k: 'explorer', l: 'Activity Explorer' },
    { k: 'timeline', l: 'Timeline' },
    ...(canMap ? [{ k: 'mapping', l: 'Mapping Studio' }] : []),
    { k: 'revisions', l: 'Revisions' }, { k: 'coverage', l: 'Evidence Coverage' },
  ];

  const [tab, setTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [activeScheduleId, setActiveScheduleId] = useState('');
  const [preset, setPreset] = useState(null);
  const [openActivity, setOpenActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  // revisions
  const [revA, setRevA] = useState(''); const [revB, setRevB] = useState(''); const [compare, setCompare] = useState(null);
  // coverage
  const [covSid, setCovSid] = useState(''); const [coverage, setCoverage] = useState(null);

  const loadBase = useCallback(async () => {
    const [d, list] = await Promise.all([
      axios.get(`${API}/projects/${projectId}/schedule-dashboard`).then((r) => r.data),
      axios.get(`${API}/projects/${projectId}/schedules`).then((r) => r.data),
    ]);
    setDashboard(d); setSchedules(list);
    const active = (d.activeSchedule && d.activeSchedule.id) || (list.find((s) => s.status === 'ACTIVE') || list[0] || {}).id || '';
    setActiveScheduleId((prev) => prev || active);
    setCovSid((prev) => prev || active);
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    loadBase().catch(() => toast.error('Failed to load schedule data')).finally(() => setLoading(false));
  }, [loadBase]);

  const goField = (kind) => {
    const now = new Date();
    if (kind === 'overdue') setPreset({ overdue: true, _k: Date.now() });
    else if (kind === 'today') {
      const s = new Date(now); s.setHours(0, 0, 0, 0);
      const e = new Date(now); e.setHours(23, 59, 59, 0);
      setPreset({ dateFrom: s.toISOString(), dateTo: e.toISOString(), _k: Date.now() });
    } else if (kind === 'week') {
      const e = new Date(now.getTime() + 7 * 86400000);
      setPreset({ dateFrom: now.toISOString(), dateTo: e.toISOString(), _k: Date.now() });
    }
    setTab('explorer');
  };

  const runCompare = async () => {
    if (!revA || !revB) { toast.error('Pick two schedules'); return; }
    try { setCompare((await axios.get(`${API}/projects/${projectId}/schedules/compare`, { params: { base: revA, target: revB } })).data); }
    catch (e) { toast.error('Compare failed'); }
  };

  const loadCoverage = async (sid) => {
    if (!sid) return;
    try { setCoverage((await axios.get(`${API}/projects/${projectId}/schedules/${sid}/evidence-coverage`)).data); }
    catch (e) { toast.error('Coverage failed'); }
  };
  useEffect(() => { if (tab === 'coverage' && covSid) loadCoverage(covSid); }, [tab, covSid]); // eslint-disable-line

  const active = dashboard?.activeSchedule;
  const kpis = dashboard ? [
    ['Total Activities', dashboard.totalActivities], ['Not Started', dashboard.notStarted],
    ['In Progress', dashboard.inProgress], ['Completed', dashboard.completed],
    ['Overdue', dashboard.overdue], ['Due This Week', dashboard.dueThisWeek],
    ['Unmapped', dashboard.unmapped], ['With Evidence', dashboard.withEvidence],
    ['Without Evidence', Math.max(0, (dashboard.totalActivities || 0) - (dashboard.withEvidence || 0))],
    ['With BOQ Mapping', dashboard.withBoqMapping],
  ] : [];

  return (
    <Layout>
      <div className="space-y-6">
        <button onClick={() => navigate('/app/projects')} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700" data-testid="schedule-back"><ChevronLeft className="w-4 h-4" /> Projects</button>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="w-7 h-7 text-blue-600" /> Schedule Intelligence</h1>
            <p className="text-slate-600 mt-1">Connect the construction schedule (WHEN) to visual evidence and BOQ quantities.</p>
          </div>
          {isAdmin && (
            <button onClick={() => navigate(`/app/projects/${projectId}/schedule-import`)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2" data-testid="schedule-import-btn"><Upload className="w-4 h-4" /> Import Schedule</button>
          )}
        </div>

        <div className="flex gap-2 border-b border-slate-200 overflow-x-auto" data-testid="schedule-tabs">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => { if (t.k === 'explorer') setPreset(null); setTab(t.k); }} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.k ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`schedule-tab-${t.k}`}>{t.l}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
        ) : tab === 'dashboard' ? (
          <>
            {!active ? (
              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-sm text-slate-600" data-testid="schedule-no-active">No ACTIVE schedule yet. {isAdmin ? 'Import and activate a schedule to begin.' : 'Ask an admin to import a schedule.'}</div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-wrap gap-x-8 gap-y-2 text-sm" data-testid="schedule-active-meta">
                <div><p className="text-xs text-slate-400">Active Schedule</p><p className="font-semibold text-slate-800">{active.name}</p></div>
                <div><p className="text-xs text-slate-400">Version / Revision</p><p className="font-medium text-slate-700">{fmt(active.version)} · {fmt(active.revision)}</p></div>
                <div><p className="text-xs text-slate-400">Schedule Data Date</p><p className="font-medium text-slate-700">{dt(active.dataDate)}</p></div>
                <div><p className="text-xs text-slate-400">Last Import</p><p className="font-medium text-slate-700">{dt(active.createdAt)}</p></div>
                <div><p className="text-xs text-slate-400">Source Type</p><p className="font-medium text-slate-700">{fmt(active.sourceType)}</p></div>
                {active.sourceType === 'P6_XER' && (
                  <div data-testid="p6-provenance-badge"><p className="text-xs text-slate-400">Source</p><p className="font-medium inline-flex items-center gap-1 text-cyan-700"><span className="px-1.5 py-0.5 rounded bg-cyan-100 text-[11px] font-semibold">Primavera P6</span>{active.sourceExternalProjectId ? `· ${active.sourceExternalProjectId}` : ''}{active.lastSyncAt ? ` · synced ${dt(active.lastSyncAt)}` : ''}</p></div>
                )}
                <div className="ml-auto text-right"><p className="text-xs text-slate-400">Current System Date</p><p className="font-medium text-slate-700">{dt(new Date().toISOString())}</p></div>
              </div>
            )}

            {/* Field drill-downs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="field-views">
              <button onClick={() => goField('today')} className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-blue-400 hover:shadow-sm transition-all flex items-center gap-3" data-testid="field-today">
                <Sun className="w-6 h-6 text-amber-500" /><div><p className="font-semibold text-slate-800">Today's Activities</p><p className="text-xs text-slate-500">Active for {dt(new Date().toISOString())}</p></div>
              </button>
              <button onClick={() => goField('week')} className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-blue-400 hover:shadow-sm transition-all flex items-center gap-3" data-testid="field-week">
                <CalendarDays className="w-6 h-6 text-blue-500" /><div><p className="font-semibold text-slate-800">This Week</p><p className="text-xs text-slate-500">Next 7 days</p></div>
              </button>
              <button onClick={() => goField('overdue')} className="bg-white rounded-xl border border-slate-200 p-4 text-left hover:border-red-400 hover:shadow-sm transition-all flex items-center gap-3" data-testid="field-overdue">
                <AlertTriangle className="w-6 h-6 text-red-500" /><div><p className="font-semibold text-slate-800">Overdue</p><p className="text-xs text-slate-500">{dashboard?.overdue || 0} past planned finish</p></div>
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="schedule-kpis">
              {kpis.map(([label, val]) => (
                <div key={label} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                  <p className="text-2xl font-bold text-slate-900">{fmt(val)}</p>
                  <p className="text-xs text-slate-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">Counts are factual. ReasonVisionX does not compute a visual/AI progress % in this phase.</p>
          </>
        ) : tab === 'explorer' ? (
          <ActivityExplorer key={`exp-${preset?._k || 'none'}`} projectId={projectId} scheduleId={activeScheduleId} preset={preset} onOpenActivity={setOpenActivity} />
        ) : tab === 'timeline' ? (
          <ScheduleTimeline projectId={projectId} scheduleId={activeScheduleId} onOpenActivity={setOpenActivity} />
        ) : tab === 'mapping' ? (
          <ActivityMappingStudio projectId={projectId} scheduleId={activeScheduleId} onOpenActivity={setOpenActivity} />
        ) : tab === 'revisions' ? (
          <div className="space-y-4" data-testid="schedule-revisions">
            <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-xl p-4">
              <select value={revA} onChange={(e) => setRevA(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="rev-a-select">
                <option value="">Base schedule</option>
                {schedules.map((s) => <option key={s.id} value={s.id} disabled={s.id === revB}>{s.name} — {s.revision} ({s.status})</option>)}
              </select>
              <span className="text-slate-400">→</span>
              <select value={revB} onChange={(e) => setRevB(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="rev-b-select">
                <option value="">Target schedule</option>
                {schedules.map((s) => <option key={s.id} value={s.id} disabled={s.id === revA}>{s.name} — {s.revision} ({s.status})</option>)}
              </select>
              <button onClick={runCompare} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm" data-testid="rev-compare-btn">Compare</button>
            </div>
            {compare && (
              <div className="space-y-3" data-testid="rev-compare-result">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-emerald-600">{compare.summary.added}</p><p className="text-xs text-slate-500">Added activities</p></div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-red-500">{compare.summary.removed}</p><p className="text-xs text-slate-500">Removed activities</p></div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-blue-600">{compare.summary.changed}</p><p className="text-xs text-slate-500">Changed</p></div>
                </div>
                {compare.changed.length > 0 && (
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <h4 className="font-semibold text-slate-800 mb-2">Changed Activities</h4>
                    {compare.changed.map((c) => (
                      <div key={c.activityCode} className="border-b border-slate-50 py-2 text-sm" data-testid="rev-changed-row">
                        <span className="font-medium">{c.activityCode}</span> <span className="text-slate-500">{c.activityName}</span>
                        {Object.entries(c.diffs).map(([field, vals]) => (
                          <div key={field} className="text-xs text-slate-500 ml-2">{field}: <span className="text-red-500">{JSON.stringify(vals[0])}</span> → <span className="text-emerald-600">{JSON.stringify(vals[1])}</span></div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
                {compare.added.length > 0 && <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm" data-testid="rev-added"><h4 className="font-semibold text-emerald-700 mb-1">Added</h4>{compare.added.slice(0, 100).map((i) => <div key={i.id}>{i.activityCode} · {i.activityName}</div>)}</div>}
                {compare.removed.length > 0 && <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm" data-testid="rev-removed"><h4 className="font-semibold text-red-600 mb-1">Removed</h4>{compare.removed.slice(0, 100).map((i) => <div key={i.id}>{i.activityCode} · {i.activityName}</div>)}</div>}
              </div>
            )}
          </div>
        ) : tab === 'coverage' ? (
          <div className="space-y-4" data-testid="schedule-coverage">
            <div className="flex items-center gap-3 flex-wrap">
              <select value={covSid} onChange={(e) => setCovSid(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="coverage-schedule-select">
                {schedules.length === 0 && <option value="">No schedules</option>}
                {schedules.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.revision} ({s.status})</option>)}
              </select>
            </div>
            {coverage && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-900">{coverage.totalActivities}</p><p className="text-xs text-slate-500">Activities</p></div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-emerald-600">{coverage.activitiesWithEvidence}</p><p className="text-xs text-slate-500">With evidence</p></div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold text-slate-400">{coverage.activitiesWithoutEvidence}</p><p className="text-xs text-slate-500">No evidence</p></div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="coverage-table">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="px-4 py-2">Activity</th><th className="px-3 py-2 text-right">Evidence Count</th><th className="px-3 py-2">Last Evidence</th><th className="px-3 py-2">Status</th></tr></thead>
                    <tbody>
                      {coverage.perActivity.length === 0 ? <tr><td colSpan="4" className="px-4 py-8 text-center text-slate-400">No activities.</td></tr> :
                        coverage.perActivity.map((r) => (
                          <tr key={r.activityId} onClick={() => setOpenActivity(r.activityId)} className="border-b border-slate-50 hover:bg-blue-50 cursor-pointer" data-testid="coverage-row">
                            <td className="px-4 py-3 text-slate-800">{r.activityCode ? `${r.activityCode} · ` : ''}{r.activityName}</td>
                            <td className="px-3 py-3 text-right">{r.evidenceCount}</td>
                            <td className="px-3 py-3 text-slate-600">{dt(r.lastEvidenceDate)}</td>
                            <td className="px-3 py-3">{r.hasEvidence ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Has evidence</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">No Evidence</span>}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-slate-400">“No Evidence” is a factual statement about linked records — it does not mean 0% progress.</p>
              </>
            )}
          </div>
        ) : null}
      </div>

      {openActivity && (
        <ActivityDetailDrawer projectId={projectId} activityId={openActivity} canMap={canMap} canLinkEvidence={true}
          onClose={() => setOpenActivity(null)} onChanged={() => loadBase()} />
      )}
    </Layout>
  );
};

export default ScheduleIntelligence;
